import { useLayoutEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import {
  AppVimeoGalleryPopupCloseIcon as CloseIconLib,
  AppClickToCallPhone as PhoneIconLib
} from '@elfsight/icons';
import { Show } from '@elfsight/lib-utils';
import { changeTone } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import classNames from 'classnames';
import { meta } from '../../meta';

export type ButtonProps = {
  onClick: () => void;
  show: boolean;
};

export function Button({ onClick, show }: ButtonProps) {
  const [addAnimation, toggleAnimation] = useState(false);
  const [needToAnimate, setNeedToAnimate] = useState(!show);
  const [textWidth, setTextWidth] = useState(0);
  const textRef = useRef(null);

  const {
    buttonIconVisible,
    buttonText,
    buttonBackgroundColor,
    buttonTextColor,
    buttonAnimationInterval,
    buttonAnimationEnabled,
    buttonSize,
    deactivate
  } = useSettings();

  const hoverBackgroundColor = buttonBackgroundColor
    ? changeTone({
        color: buttonBackgroundColor,
        amount: 0.1,
        darken: true
      })
    : '';

  useLayoutEffect(() => {
    setNeedToAnimate(!show);
  }, [show]);

  useLayoutEffect(() => {
    if (!addAnimation) {
      setTimeout(() => {
        toggleAnimation(buttonAnimationEnabled);

        setTimeout(() => {
          toggleAnimation(false);
        }, 2000);
      }, buttonAnimationInterval * 1000);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [addAnimation, buttonAnimationEnabled]);

  useLayoutEffect(() => {
    if (buttonText) {
      setTimeout(() => {
        const {
          current: { offsetWidth: width }
        } = textRef;

        setTextWidth(width);
      }, 300);
    }
  }, [buttonText, buttonSize, textRef?.current?.offsetWidth]);

  const shouldAnimate = addAnimation && needToAnimate && !deactivate;
  const defaultIconContainerSize = `${buttonSize / 2.5}px`;

  const iconContainerSize = (() => {
    if (!!buttonText && !show) {
      return buttonIconVisible ? defaultIconContainerSize : '0';
    }

    return defaultIconContainerSize;
  })();

  return (
    <Component
      onClick={onClick}
      onKeyPress={onClick}
      role="button"
      tabIndex="0"
      _animate={shouldAnimate}
      _backgroundColor={buttonBackgroundColor}
      _borderColor={buttonBackgroundColor}
      _hasText={!!buttonText}
      _hoverBackgroundColor={hoverBackgroundColor}
      _show={show}
      _size={buttonSize}
    >
      <IconContainer
        _iconContainerSize={iconContainerSize}
        _fillColor={buttonTextColor}
      >
        <Show when={buttonIconVisible}>
          <ButtonIcon _show={show} _animate={shouldAnimate} />
        </Show>

        <CloseIcon focusable="false" _show={show} />
      </IconContainer>

      <Show when={!!buttonText}>
        <Text
          _color={buttonTextColor}
          _size={buttonSize}
          _width={textWidth}
          _show={show}
          _iconVisible={buttonIconVisible}
        >
          {buttonText}

          <TextClone ref={textRef}>{buttonText}</TextClone>
        </Text>
      </Show>
    </Component>
  );
}

const pulseSmall = keyframes`
  0% {
    transform: scale(0.6);
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: scale(1.2);
    opacity: 0;
  }
`;

const pulseFull = keyframes`
  0% {
    transform: scale(0.6);
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
`;

const phone = keyframes`
  0% { transform: rotateZ(0); }
  2% { transform: rotateZ(6deg); }
  4% { transform: rotateZ(-6deg); }
  6% { transform: rotateZ(6deg); }
  8% { transform: rotateZ(-6deg); }
  10% { transform: rotateZ(6deg); }
  12% { transform: rotateZ(-6deg); }
  14% { transform: rotateZ(6deg); }
  16% { transform: rotateZ(-6deg); }
  18% { transform: rotateZ(6deg); }
  20% { transform: rotateZ(-6deg); }
  22% { transform: rotateZ(6deg); }
  24% { transform: rotateZ(-6deg); }
  26% { transform: rotateZ(6deg); }
  28% { transform: rotateZ(-6deg); }
  30% { transform: rotateZ(6deg); }
  32% { transform: rotateZ(-6deg); }
  34% { transform: rotateZ(6deg); }
  36% { transform: rotateZ(-6deg); }
  38% { transform: rotateZ(6deg); }
  40% { transform: rotateZ(-6deg); }
  42% { transform: rotateZ(6deg); }
  44% { transform: rotateZ(-6deg); }
  46% { transform: rotateZ(6deg); }
`;

const Component = styled.div.attrs(({ _animate, _hasText }) => ({
  className: classNames(`${meta.prefix}-button-component`, {
    [`${meta.prefix}-button-animate`]: _animate,
    [`${meta.prefix}-button-animateSmall`]: _hasText,
    [`${meta.prefix}-button-animateFull`]: !_hasText
  })
}))`
  position: relative;
  border-radius: 500px;
  display: inline-flex;
  align-items: center;
  text-decoration: none !important;
  justify-content: center;
  outline: none;
  box-shadow:
    0 0 0 0 rgba(52, 152, 219, 0),
    0 5px 10px rgba(0, 0, 0, 0.2);
  z-index: 999999997;
  transition: all 0.3s ease;
  cursor: pointer;
  background-color: ${({ _backgroundColor }) => _backgroundColor};
  font-size: ${({ _size }) => _size / 2.5}px;
  padding: ${({ _size, _hasText, _show }) =>
    !_hasText || _show ? `${_size / 3}px` : `${_size / 3}px ${_size / 1.68}px`};

  &:before,
  &:after {
    content: '';
    position: absolute;
    border: 1px solid ${({ _borderColor }) => _borderColor || '#eee'};
    left: -2px;
    right: -2px;
    top: -2px;
    bottom: -2px;
    border-radius: 500px;
    opacity: 0;
    z-index: 0;
  }

  &:hover {
    background-color: ${({ _hoverBackgroundColor }) =>
      _hoverBackgroundColor || ''};
  }

  ${({ _animate, _hasText }) =>
    _animate &&
    css`
      &:before,
      &:after {
        animation: ${_hasText ? pulseSmall : pulseFull} 1.5s linear infinite;
      }

      &:after {
        animation-delay: 0.5s;
      }
    `}
`;

const IconContainer = styled.div.attrs({
  className: `${meta.prefix}-button-iconContainer`
})`
  position: relative;
  width: ${({ _iconContainerSize }) => _iconContainerSize};
  height: ${({ _iconContainerSize }) => _iconContainerSize};

  & svg {
    display: flex;
    width: 100%;
    height: 100%;
    fill: ${({ _fillColor }) => _fillColor};
  }
`;

const iconMixin = css`
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  z-index: 1;
  transition: 0.3s ease all;
  position: absolute;
  top: 0;
  left: 0;
`;

const ButtonIcon = styled(PhoneIconLib).attrs(({ _show }) => ({
  className: classNames(`${meta.prefix}-button-icon`, {
    [`${meta.prefix}-button-hide`]: _show
  })
}))`
  ${iconMixin}
  transform: translate3d(0, 0, 0);
  opacity: ${({ _show }) => (_show ? 0 : 1)};

  ${({ _animate }) =>
    _animate &&
    css`
      animation: ${phone} 3000ms infinite;
    `}
`;

const CloseIcon = styled(CloseIconLib).attrs(({ _show }) => ({
  className: classNames(`${meta.prefix}-button-closeIcon`, {
    [`${meta.prefix}-button-show`]: _show
  })
}))`
  ${iconMixin}
  opacity: ${({ _show }) => (_show ? 1 : 0)};
`;

const Text = styled.div.attrs({
  className: `${meta.prefix}-button-text`
})`
  line-height: 1.25;
  font-size: ${({ _size }) => _size / 2.5}px;
  font-weight: 700;
  z-index: 1;
  transition: 0.3s ease all;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  color: ${({ _color }) => _color};
  width: ${({ _width, _show }) => (_show ? '0' : `${_width}px`)};
  margin-left: ${({ _iconVisible }) => (_iconVisible ? '12px' : '0')};
  ${({ _show }) =>
    _show &&
    css`
      width: 0;
      height: 0;
      margin: 0;
      padding: 0;
      opacity: 0;
    `}
`;

const TextClone = styled.div.attrs({
  className: `${meta.prefix}-button-textClone`
})`
  position: absolute;
  left: 0;
  top: 0;
  white-space: nowrap;
  opacity: 0;
`;
